import AiStarIcon from "@redotech/redo-icons/ai-star-orange.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { aiSubjectLineTones } from "@redotech/redo-model/ai-subject-line-tones";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as styles from "./ai-suggestions.module.css";

interface AiEmailPreviewSuggestionsProps {
  emailPreview: string;
  subjectLine?: string;
  onSuggestionClick: (suggestion: string) => void;
  getAiEmailPreviewSuggestions: (params: {
    text: string;
    tone?: string;
    subjectLine?: string;
  }) => Promise<{ text: string; tone: string }[]>;
}

export const AiEmailPreviewSuggestions = memo(
  function AiEmailPreviewSuggestions(props: AiEmailPreviewSuggestionsProps) {
    const {
      emailPreview,
      subjectLine,
      onSuggestionClick,
      getAiEmailPreviewSuggestions,
    } = props;

    const [selectedTone, setSelectedTone] = useState<string | null>(null);
    const [aiSuggestions, setAiSuggestions] = useState<
      { text: string; tone: string }[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [shouldShowPanel, setShouldShowPanel] = useState(false);
    const [scrollPercentage, setScrollPercentage] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const toneButtonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const previousEmailPreviewRef = useRef<string>(emailPreview);
    const suggestionSelectedRef = useRef<boolean>(false);
    const isInitialMount = useRef<boolean>(true);
    const hasUserEditedEmailPreviewRef = useRef<boolean>(false);
    const isLoadingRef = useRef<boolean>(false);

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const scrollLeft = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        const percentage = maxScroll > 0 ? scrollLeft / maxScroll : 0;
        setScrollPercentage(percentage);
      }
    };

    const scrollToSelectedTone = useCallback(() => {
      if (!selectedTone || !scrollContainerRef.current) {
        return;
      }

      const selectedButton = toneButtonRefs.current[selectedTone];
      if (selectedButton) {
        selectedButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }, [selectedTone]);

    const handleScrollbarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
      const scrollbar = e.currentTarget;
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }

      const startX = e.clientX;
      const thumbWidth = 38;
      const maxThumbLeft = scrollbar.clientWidth - thumbWidth;
      const startThumbLeft = scrollPercentage * maxThumbLeft;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const newThumbLeft = Math.max(
          0,
          Math.min(maxThumbLeft, startThumbLeft + deltaX),
        );
        const newPercentage = newThumbLeft / maxThumbLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = newPercentage * maxScroll;
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const loadSuggestions = useCallback(
      async (text: string, tone?: string) => {
        if (!getAiEmailPreviewSuggestions || !text) {
          return;
        }

        if (isLoadingRef.current) {
          return;
        }

        isLoadingRef.current = true;
        setIsLoading(true);
        setAiSuggestions([]);

        try {
          const suggestions = await getAiEmailPreviewSuggestions({
            text,
            tone,
            subjectLine,
          });
          setAiSuggestions(suggestions);
          setIsLoading(false);
          isLoadingRef.current = false;
        } catch (error) {
          console.error("[AI Preview Suggestions] Failed to load:", error);
          setShouldShowPanel(false);
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      },
      [getAiEmailPreviewSuggestions, subjectLine],
    );

    useEffect(() => {
      if (shouldShowPanel && !isLoading) {
        setTimeout(() => {
          scrollToSelectedTone();
        }, 100);
      }
    }, [selectedTone, shouldShowPanel, isLoading, scrollToSelectedTone]);

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        previousEmailPreviewRef.current = emailPreview;
        return;
      }

      if (suggestionSelectedRef.current) {
        suggestionSelectedRef.current = false;
        previousEmailPreviewRef.current = emailPreview;
        hasUserEditedEmailPreviewRef.current = true;
        return;
      }

      if (emailPreview !== previousEmailPreviewRef.current) {
        hasUserEditedEmailPreviewRef.current = true;

        if (isDismissed) {
          setIsDismissed(false);
        }

        setAiSuggestions([]);
        if (shouldShowPanel) {
          setIsLoading(true);
        }
      }
      previousEmailPreviewRef.current = emailPreview;

      if (!emailPreview || !getAiEmailPreviewSuggestions) {
        setAiSuggestions([]);
        setShouldShowPanel(false);
        return;
      }

      if (!hasUserEditedEmailPreviewRef.current) {
        return;
      }

      const timeoutId = setTimeout(() => {
        setShouldShowPanel(true);
        void loadSuggestions(emailPreview, selectedTone || undefined);
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailPreview, isDismissed, selectedTone]);

    const shouldShow =
      !isDismissed && emailPreview.length > 0 && shouldShowPanel;

    if (!shouldShow) {
      return null;
    }

    const hasPaddingBottom = isLoading || aiSuggestions.length === 0;

    return (
      <div className={styles.container}>
        <Flex
          className={`${styles.suggestionsPanel} ${hasPaddingBottom ? styles.hasPaddingBottom : ""}`}
          dir="column"
          gap="sm"
          p="none"
        >
          <Flex dir="column" gap="lg" w="full">
            <Flex
              align="center"
              dir="row"
              justify="space-between"
              pl="md"
              pr="md"
              w="full"
            >
              <Flex align="center" gap="sm">
                <AiStarIcon
                  className={`${styles.aiStarIcon} ${isLoading ? styles.spinning : ""}`}
                />
                <Text fontSize="xs" textColor="primary">
                  {isLoading
                    ? "Generating suggestions..."
                    : "Preview line suggestions"}
                </Text>
              </Flex>
              <div
                className={styles.dismissButton}
                onClick={() => {
                  setIsDismissed(true);
                }}
              >
                <XIcon height="14" width="14" />
              </div>
            </Flex>
            {!isLoading && (
              <>
                <div
                  className={styles.tonesContainer}
                  onScroll={handleScroll}
                  ref={scrollContainerRef}
                >
                  {aiSubjectLineTones.map((tone) => {
                    const isSelected = selectedTone === tone;
                    return (
                      <div
                        className={`${styles.tonePill} ${isSelected ? styles.selected : styles.unselected}`}
                        key={tone}
                        onClick={() => {
                          const newTone = selectedTone === tone ? null : tone;
                          setSelectedTone(newTone);
                          void loadSuggestions(
                            emailPreview,
                            newTone || undefined,
                          );
                        }}
                        ref={(el) => {
                          toneButtonRefs.current[tone] = el;
                        }}
                      >
                        {tone}
                      </div>
                    );
                  })}
                </div>
                <div
                  className={styles.scrollbarTrack}
                  onMouseDown={handleScrollbarDrag}
                >
                  <div
                    className={styles.scrollbarThumb}
                    style={{
                      left: scrollContainerRef.current?.clientWidth
                        ? `${scrollPercentage * (100 - (40 / scrollContainerRef.current.clientWidth) * 100)}%`
                        : "0%",
                    }}
                  />
                </div>
              </>
            )}
            {!isLoading && aiSuggestions.length > 0 && (
              <Flex dir="column" gap="md" w="full">
                {aiSuggestions.map((suggestion, index) => {
                  const isLastItem = index === aiSuggestions.length - 1;
                  return (
                    <div key={index}>
                      <Flex
                        align="center"
                        className={`${styles.suggestionItem} ${isLastItem ? styles.last : ""}`}
                        justify="space-between"
                        onClick={() => {
                          suggestionSelectedRef.current = true;
                          onSuggestionClick(suggestion.text);
                          setAiSuggestions([]);
                          setShouldShowPanel(false);
                        }}
                        w="full"
                      >
                        <Text fontSize="xs" px="md" py="xs" textColor="primary">
                          {suggestion.text}
                        </Text>
                      </Flex>
                    </div>
                  );
                })}
              </Flex>
            )}
          </Flex>
        </Flex>
      </div>
    );
  },
);
